import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import routing from "routing";
import * as i18next from "i18next";
import { SEARCH_PATH_NAMESPACE } from "constants/i18n-namespaces";
import {
  getBiller,
  getSelectedPayableType,
  getPayableSession,
  getLabelsWithKrn,
  getIsMobile,
} from "selectors";
import FundingOption from "constants/FundingOption";
import { setPayableSession } from "actions/payableSessionActions";
import { ACTION } from "actions/paymentIntentActions";
import { getLabelWithKrn } from "utilities/helper";
import { Breadcrumb, BreadcrumbItem } from "../breadcrumb";
import { EMPTY_WALLET_ITEMS } from "actions/walletActions";

type CurrentStep =
  | "select-item"
  | "lookup"
  | "location"
  | "quantity"
  | "payable-amount"
  | "invoice-list"
  | "details"
  | "payment"
  | "review"
  | "barcode";

export interface PayableBreadcrumbProps {
  className?: string;
  isCashView?: boolean;
}

export const PayableBreadcrumb: React.FC<PayableBreadcrumbProps> = ({
  className,
  isCashView = false,
}) => {
  const { t } = useTranslation(SEARCH_PATH_NAMESPACE);
  const dispatch = useDispatch();
  const location = useLocation();

  const isMobile = useSelector(getIsMobile);

  const payableSession = useSelector(getPayableSession);
  const payableTypeFromStore = useSelector(getSelectedPayableType);

  const labelSourceWithKrn = useSelector(getLabelsWithKrn) as {
    labelsWithKrn: any;
  };
  const biller = useSelector(getBiller);

  if (!biller) {
    return null;
  }

  const {
    payableType: payableTypeFromSession,
    payable,
    lookupResults,
  } = payableSession || {};

  const payableType = payableTypeFromSession ?? payableTypeFromStore;
  const category = payableType.category.toLowerCase();
  const isFreeform = category === "freeform";
  const isOrder = category === "order";
  const isRcpOnly =
    !payableSession?.paymentIntentId &&
    payableSession?.fundingOptions?.length === 1 &&
    payableSession?.fundingOptions[0] === FundingOption.Cash;
  const hasMultipleBusinessUnitsForOrder =
    isOrder &&
    Array.isArray(payableType?.supportedBusinessUnitCodes) &&
    payableType?.supportedBusinessUnitCodes.length > 1;
  const isMaxQuantityOne = isOrder && payableType?.maxQuantity === 1;

  const getCurrentStepFromUrl = (): CurrentStep => {
    const pathname = location.pathname;

    if (pathname === routing.index()) {
      return "select-item";
    } else if (pathname === routing.lookup.index()) {
      return "lookup";
    } else if (pathname === routing.invoiceList.index()) {
      return "invoice-list";
    } else if (pathname === routing.payableCreateOrder.index()) {
      return hasMultipleBusinessUnitsForOrder ? "location" : "quantity";
    } else if (pathname === routing.payableDetails.index()) {
      return "details";
    } else if (pathname === routing.payableDashboard.index()) {
      return "payment";
    } else if (pathname === routing.payableReview.index()) {
      return "review";
    } else if (pathname === routing.payableCash.index()) {
      return "barcode";
    } else if (pathname === routing.payableAmount.index()) {
      return "payable-amount";
    } else {
      return "select-item";
    }
  };

  const currentStep = getCurrentStepFromUrl();

  const shouldShowSearch = () => {
    return (
      biller.payableTypeDefinitions && biller.payableTypeDefinitions.length > 1
    );
  };

  const shouldShowInvoiceList = () => {
    return !!(
      payableType?.category.toLowerCase() === "invoice" &&
      lookupResults &&
      lookupResults.length > 1
    );
  };

  const shouldShowDetails = () => {
    if (!payable || !payableType) return false;

    const hasExtensionFields =
      payableType.extensionFields && payableType.extensionFields.length > 0;

    return (
      (category === "invoice" ||
        category === "order" ||
        category === "freeform") &&
      hasExtensionFields
    );
  };

  const getPayableTypeLabel = () => {
    if (!payableType) return "";
    return (
      getLabelWithKrn(
        labelSourceWithKrn.labelsWithKrn,
        payableSession?.payableType?.typeKrn ?? null,
        payableType.name
      ) || payableType.name
    );
  };

  const handleSearchClick = () => {
    dispatch(setPayableSession(undefined));
    dispatch({ type: EMPTY_WALLET_ITEMS });
  };

  const handleLookupClick = () => {
    if (shouldShowInvoiceList()) {
      dispatch({ type: ACTION.RESET_PAYMENT_INTENT_STATE });
      dispatch({ type: EMPTY_WALLET_ITEMS });
    } else {
      dispatch(setPayableSession(undefined));
      dispatch({ type: EMPTY_WALLET_ITEMS });
    }
  };

  const handleAmountClick = () => {
    dispatch({ type: EMPTY_WALLET_ITEMS });
  };

  const handlePaymentClick = () => {
    dispatch({ type: EMPTY_WALLET_ITEMS });
    dispatch({ type: ACTION.RESET_PAYMENT_INTENT_STATE });
    dispatch({ type: EMPTY_WALLET_ITEMS });
  };

  type StepOrder = CurrentStep[];
  type RoutingType = typeof routing;

  function getSearchBreadcrumb(
    currentStep: CurrentStep,
    currentStepIndex: number,
    stepOrder: StepOrder,
    t: i18next.TFunction,
    routing: RoutingType,
    handleSearchClick: () => void,
    shouldShowSearch: () => boolean
  ): BreadcrumbItem[] {
    if (shouldShowSearch() && currentStepIndex >= 0) {
      return [
        {
          label: t("select-item"),
          dataCy: "select-item",
          to: currentStep !== "select-item" ? routing.index() : undefined,
          onClick:
            currentStep === "select-item" ? undefined : handleSearchClick,
          isActive: currentStep === "select-item",
        },
      ];
    }
    return [];
  }

  function getLookupBreadcrumb(
    currentStep: CurrentStep,
    currentStepIndex: number,
    stepOrder: StepOrder,
    t: i18next.TFunction,
    routing: RoutingType,
    handleLookupClick: () => void,
    payableType: any
  ): BreadcrumbItem[] {
    const payableCategory = payableType?.category?.toLowerCase() ?? "";
    if (
      payableCategory !== "freeform" &&
      payableCategory !== "order" &&
      currentStepIndex >= stepOrder.indexOf("lookup")
    ) {
      return [
        {
          label:
            payableCategory === "account"
              ? t("locate")
              : t("search"),
          dataCy: payableCategory === "account"
              ? "locate"
              : "search",
          to: currentStep !== "lookup" ? routing.lookup.index() : undefined,
          onClick: currentStep !== "lookup" ? handleLookupClick : undefined,
          isActive: currentStep === "lookup",
        },
      ];
    }
    return [];
  }

  function getAmountBreadcrumb(
    currentStep: CurrentStep,
    currentStepIndex: number,
    stepOrder: StepOrder,
    t: i18next.TFunction,
    routing: RoutingType,
    handleAmountClick: () => void,
    isFreeform: boolean
  ): BreadcrumbItem[] {
    if (isFreeform && currentStepIndex >= stepOrder.indexOf("payable-amount")) {
      return [
        {
          label: t("amount"),
          dataCy: "amount",
          to:
            currentStep !== "payable-amount"
              ? routing.payableAmount.index()
              : undefined,
          onClick:
            currentStep === "payable-amount" ? undefined : handleAmountClick,
          isActive: currentStep === "payable-amount",
        },
      ];
    }
    return [];
  }

  function getInvoiceListBreadcrumb(
    currentStep: CurrentStep,
    currentStepIndex: number,
    stepOrder: StepOrder,
    shouldShowInvoiceList: () => boolean,
    getPayableTypeLabel: () => string,
    routing: RoutingType
  ): BreadcrumbItem[] {
    if (
      shouldShowInvoiceList() &&
      currentStepIndex >= stepOrder.indexOf("invoice-list")
    ) {
      return [
        {
          label: getPayableTypeLabel(),
          dataCy: "invoice-type",
          to:
            currentStep !== "invoice-list"
              ? routing.invoiceList.index()
              : undefined,
          onClick: undefined,
          isActive: currentStep === "invoice-list",
        },
      ];
    }
    return [];
  }

  function getOrderStepBreadcrumb(
    currentStep: CurrentStep,
    currentStepIndex: number,
    stepOrder: StepOrder,
    t: i18next.TFunction,
    category: string,
    isMaxQuantityOne: boolean,
    hasMultipleBusinessUnitsForOrder: boolean
  ): BreadcrumbItem[] {
    if (
      category === "order" &&
      (!isMaxQuantityOne || hasMultipleBusinessUnitsForOrder)
    ) {
      const orderStep: CurrentStep = hasMultipleBusinessUnitsForOrder
        ? "location"
        : "quantity";
      if (
        stepOrder.includes(orderStep) &&
        currentStepIndex >= stepOrder.indexOf(orderStep)
      ) {
        return [
          {
            label: hasMultipleBusinessUnitsForOrder
              ? t("location")
              : t("quantity"),
            dataCy: hasMultipleBusinessUnitsForOrder
              ? "location"
              : "quantity",
            to:
              currentStep === orderStep
                ? undefined : routing.payableCreateOrder.index(),
            onClick: undefined,
            isActive: currentStep === orderStep,
          },
        ];
      }
    }
    return [];
  }

  function getDetailsBreadcrumb(
    currentStep: CurrentStep,
    currentStepIndex: number,
    stepOrder: StepOrder,
    t: i18next.TFunction,
    routing: RoutingType,
    shouldShowDetails: () => boolean
  ): BreadcrumbItem[] {
    if (
      shouldShowDetails() &&
      currentStepIndex >= stepOrder.indexOf("details")
    ) {
      return [
        {
          label: t("payable-details"),
          dataCy: "details",
          to:
            currentStep !== "details"
              ? routing.payableDetails.index()
              : undefined,
          onClick: undefined,
          isActive: currentStep === "details",
        },
      ];
    }
    return [];
  }

  function getPaymentBreadcrumb(
    currentStep: CurrentStep,
    currentStepIndex: number,
    stepOrder: StepOrder,
    t: i18next.TFunction,
    routing: RoutingType,
    handlePaymentClick: () => void,
    isRcpOnly: boolean
  ): BreadcrumbItem[] {
    if (!isRcpOnly && currentStepIndex >= stepOrder.indexOf("payment")) {
      return [
        {
          label: t("payable-dashboard"),
          dataCy: "payment",
          to:
            currentStep !== "payment"
              ? routing.payableDashboard.index()
              : undefined,
          onClick: currentStep !== "payment" ? handlePaymentClick : undefined,
          isActive: currentStep === "payment",
        },
      ];
    }
    return [];
  }

  function getReviewBreadcrumb(
    currentStep: CurrentStep,
    currentStepIndex: number,
    stepOrder: StepOrder,
    t: i18next.TFunction,
    isCashView: boolean
  ): BreadcrumbItem[] {
    if (!isCashView && currentStepIndex >= stepOrder.indexOf("review")) {
      return [
        {
          label: t("review"),
          dataCy: "review",
          to: undefined,
          onClick: undefined,
          isActive: currentStep === "review",
        },
      ];
    }
    return [];
  }

  function getBarcodeBreadcrumb(
    currentStep: CurrentStep,
    currentStepIndex: number,
    stepOrder: StepOrder,
    t: i18next.TFunction,
    isCashView: boolean
  ): BreadcrumbItem[] {
    if (isCashView && currentStepIndex >= stepOrder.indexOf("barcode")) {
      return [
        {
          label: t("payable-cash"),
          to: undefined,
          onClick: undefined,
          isActive: currentStep === "barcode",
          dataCy: "barcode",
        },
      ];
    }
    return [];
  }

  const buildBreadcrumbItems = (): BreadcrumbItem[] => {
    let stepOrder: CurrentStep[];

    if (category === "order") {
      stepOrder = [
        "select-item",
        hasMultipleBusinessUnitsForOrder ? "location" : "quantity",
        "details",
        "payment",
      ];
    } else {
      stepOrder = [
        "select-item",
        "lookup",
        "payable-amount",
        "invoice-list",
        "location",
        "details",
        "payment",
      ];
    }

    !isCashView && stepOrder.push("review");
    isCashView && stepOrder.push("barcode");

    const currentStepIndex = stepOrder.indexOf(currentStep);

    return [
      ...getSearchBreadcrumb(
        currentStep,
        currentStepIndex,
        stepOrder,
        t,
        routing,
        handleSearchClick,
        shouldShowSearch
      ),
      ...getLookupBreadcrumb(
        currentStep,
        currentStepIndex,
        stepOrder,
        t,
        routing,
        handleLookupClick,
        payableType
      ),
      ...getAmountBreadcrumb(
        currentStep,
        currentStepIndex,
        stepOrder,
        t,
        routing,
        handleAmountClick,
        isFreeform
      ),
      ...getInvoiceListBreadcrumb(
        currentStep,
        currentStepIndex,
        stepOrder,
        shouldShowInvoiceList,
        getPayableTypeLabel,
        routing
      ),
      ...getOrderStepBreadcrumb(
        currentStep,
        currentStepIndex,
        stepOrder,
        t,
        category,
        isMaxQuantityOne,
        hasMultipleBusinessUnitsForOrder
      ),
      ...getDetailsBreadcrumb(
        currentStep,
        currentStepIndex,
        stepOrder,
        t,
        routing,
        shouldShowDetails
      ),
      ...getPaymentBreadcrumb(
        currentStep,
        currentStepIndex,
        stepOrder,
        t,
        routing,
        handlePaymentClick,
        isRcpOnly
      ),
      ...getReviewBreadcrumb(
        currentStep,
        currentStepIndex,
        stepOrder,
        t,
        isCashView
      ),
      ...getBarcodeBreadcrumb(
        currentStep,
        currentStepIndex,
        stepOrder,
        t,
        isCashView
      ),
    ];
  };

  const breadcrumbItems = buildBreadcrumbItems();
  return (
    <Breadcrumb
      items={breadcrumbItems}
      className={className}
      isMobile={isMobile}
    />
  );
};
